/**
 * Main application entry point
 */

import { gameReducer, initialGameState } from './game/reducer.ts';
import type { GameState, GameAction, Player } from './game/types.ts';
import { RTCConnectionManager } from './rtc/connection.ts';
import type {
  RTCMessage,
  RTCConnectionState,
  SignalingData,
} from './rtc/types.ts';
import { createGameBoard, updateGameBoard } from './ui/GameBoard.ts';
import {
  createConnectionPanel,
  showConnectionCode,
  updateConnectionStatus,
  hideConnectionPanel,
  showConnectionPanel,
} from './ui/ConnectionPanel.ts';
import {
  createGameStatus,
  updateGameStatus,
  updateConnectionStatus as updateStatusConnectionStatus,
} from './ui/GameStatus.ts';
import { validateSignalingData } from './utils/signaling.ts';
import './style.css';

/**
 * Main application class
 */
class TicTacToeApp {
  private gameState: GameState = initialGameState;
  private rtcManager: RTCConnectionManager | null = null;
  private isHost = false;

  // DOM elements
  private appElement: HTMLElement;
  private gameBoardElement!: HTMLElement;
  private connectionPanelElement!: HTMLElement;
  private gameStatusElement!: HTMLElement;

  constructor() {
    this.appElement = document.getElementById('app')!;
    this.initializeUI();
    this.setupServiceWorker();
  }

  /**
   * Initialize the user interface
   */
  private initializeUI(): void {
    this.appElement.innerHTML = `
      <div class="app-container">
        <header class="app-header">
          <h1>GP2P Tic-Tac-Toe</h1>
          <p>Decentralized peer-to-peer gaming</p>
        </header>
        
        <main class="app-main">
          <div id="connection-panel-container"></div>
          <div id="game-container" class="game-container hidden">
            <div id="game-status-container"></div>
            <div id="game-board-container"></div>
          </div>
        </main>
        
        <footer class="app-footer">
          <p>Built with WebRTC • No servers required</p>
        </footer>
      </div>
    `;

    // Create UI components
    this.connectionPanelElement = createConnectionPanel({
      onCreateGame: () => this.createGame(),
      onJoinGame: signalingData => this.joinGame(signalingData),
      onCompleteConnection: signalingData =>
        this.completeConnection(signalingData),
    });

    this.gameStatusElement = createGameStatus(this.gameState, {
      onResetGame: () => this.resetGame(),
      onNewGame: () => this.newGame(),
    });

    this.gameBoardElement = createGameBoard(this.gameState, {
      onCellClick: position => this.makeMove(position),
    });

    // Add components to DOM
    document
      .getElementById('connection-panel-container')!
      .appendChild(this.connectionPanelElement);
    document
      .getElementById('game-status-container')!
      .appendChild(this.gameStatusElement);
    document
      .getElementById('game-board-container')!
      .appendChild(this.gameBoardElement);
  }

  /**
   * Create a new game as host
   */
  private async createGame(): Promise<void> {
    try {
      this.isHost = true;
      this.rtcManager = new RTCConnectionManager({
        onConnectionStateChange: state =>
          this.handleConnectionStateChange(state),
        onMessage: message => this.handleRTCMessage(message),
        onError: error => this.handleRTCError(error),
      });

      const signalingData = await this.rtcManager.createOffer();
      showConnectionCode(this.connectionPanelElement, signalingData);
    } catch (error) {
      console.error('Failed to create game:', error);
      alert('Failed to create game. Please try again.');
    }
  }

  /**
   * Join an existing game
   */
  private async joinGame(signalingData: SignalingData): Promise<void> {
    try {
      // Validate signaling data
      if (!validateSignalingData(signalingData)) {
        throw new Error('Invalid signaling data');
      }

      this.isHost = false;
      this.rtcManager = new RTCConnectionManager({
        onConnectionStateChange: state =>
          this.handleConnectionStateChange(state),
        onMessage: message => this.handleRTCMessage(message),
        onError: error => this.handleRTCError(error),
      });

      const answerData = await this.rtcManager.acceptOffer(signalingData);
      showConnectionCode(this.connectionPanelElement, answerData);
    } catch (error) {
      console.error('Failed to join game:', error);
      alert('Failed to join game. Please try again.');
    }
  }

  /**
   * Complete the connection (host only)
   */
  private async completeConnection(
    signalingData: SignalingData
  ): Promise<void> {
    if (!this.rtcManager || !this.isHost) {
      return;
    }

    try {
      // Validate signaling data
      if (!validateSignalingData(signalingData)) {
        throw new Error('Invalid signaling data');
      }

      await this.rtcManager.completeConnection(signalingData);
    } catch (error) {
      console.error('Failed to complete connection:', error);
      alert('Failed to complete connection. Please try again.');
    }
  }

  /**
   * Handle WebRTC connection state changes
   */
  private handleConnectionStateChange(state: RTCConnectionState): void {
    updateConnectionStatus(this.connectionPanelElement, state);

    if (state === 'connected') {
      this.startGameplay();
      updateStatusConnectionStatus(this.gameStatusElement, true);
    } else if (state === 'disconnected' || state === 'failed') {
      updateStatusConnectionStatus(this.gameStatusElement, false);
      this.dispatchGameAction({ type: 'SET_WAITING' });
      this.showConnectionPanel();
    }
  }

  /**
   * Handle incoming WebRTC messages
   */
  private handleRTCMessage(message: RTCMessage): void {
    switch (message.type) {
      case 'game-move': {
        const movePayload = message.payload as {
          position: number;
          player: string;
        };
        this.dispatchGameAction({
          type: 'RECEIVE_MOVE',
          payload: {
            position: movePayload.position,
            player: movePayload.player as Player,
          },
        });
        break;
      }

      case 'game-reset':
        this.dispatchGameAction({ type: 'RESET_GAME' });
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  /**
   * Handle WebRTC errors
   */
  private handleRTCError(error: Error): void {
    console.error('RTC Error:', error);
  }

  /**
   * Start gameplay once connected
   */
  private startGameplay(): void {
    const localPlayer: Player = this.isHost ? 'X' : 'O';
    this.dispatchGameAction({
      type: 'START_GAME',
      payload: { localPlayer },
    });

    this.showGameInterface();
  }

  /**
   * Make a move in the game
   */
  private makeMove(position: number): void {
    if (!this.gameState.isMyTurn || this.gameState.status !== 'playing') {
      return;
    }

    // Dispatch local move
    this.dispatchGameAction({
      type: 'MAKE_MOVE',
      payload: { position },
    });

    // Send move to opponent
    if (this.rtcManager) {
      this.rtcManager.sendMessage({
        type: 'game-move',
        payload: {
          position,
          player: this.gameState.localPlayer,
          timestamp: Date.now(),
        },
      });
    }
  }

  /**
   * Reset the current game
   */
  private resetGame(): void {
    this.dispatchGameAction({ type: 'RESET_GAME' });

    // Send reset message to opponent
    if (this.rtcManager) {
      this.rtcManager.sendMessage({
        type: 'game-reset',
        payload: {},
      });
    }
  }

  /**
   * Start a new game (disconnect and show connection panel)
   */
  private newGame(): void {
    if (this.rtcManager) {
      this.rtcManager.close();
      this.rtcManager = null;
    }

    this.gameState = initialGameState;
    this.updateUI();
    this.showConnectionPanel();
  }

  /**
   * Dispatch game action and update UI
   */
  private dispatchGameAction(action: GameAction): void {
    this.gameState = gameReducer(this.gameState, action);
    this.updateUI();
  }

  /**
   * Update the user interface
   */
  private updateUI(): void {
    updateGameBoard(this.gameBoardElement, this.gameState, {
      onCellClick: position => this.makeMove(position),
    });
    updateGameStatus(this.gameStatusElement, this.gameState);
  }

  /**
   * Show the game interface
   */
  private showGameInterface(): void {
    hideConnectionPanel(this.connectionPanelElement);
    document.getElementById('game-container')!.classList.remove('hidden');
  }

  /**
   * Show the connection panel
   */
  private showConnectionPanel(): void {
    showConnectionPanel(this.connectionPanelElement);
    document.getElementById('game-container')!.classList.add('hidden');
  }

  /**
   * Setup service worker for PWA
   */
  private async setupServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('SW registered:', registration);
      } catch (error) {
        console.log('SW registration failed:', error);
      }
    }
  }
}

// Initialize the application when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new TicTacToeApp();
});
